import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './BuilderJS6.css';

// Load BuilderJS CSS and JS files (as per documentation)
const loadBuilderJS = () => {
  return new Promise((resolve, reject) => {
    if (window.Builder) {
      resolve();
      return;
    }

    // Ensure Material Icons are loaded (required for widget icons)
    if (!document.querySelector('link[href*="Material+Icons"]')) {
      const materialIconsLink = document.createElement('link');
      materialIconsLink.rel = 'stylesheet';
      materialIconsLink.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(materialIconsLink);
    }

    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/builderjs6/builder.css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = '/builderjs6/builder.js';
    script.onload = () => {
      if (window.Builder) {
        resolve();
      } else {
        reject(new Error('BuilderJS failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load BuilderJS script'));
    document.body.appendChild(script);
  });
};

const BuilderJS6Simple = ({ 
  initialData = null, 
  onSave = null, 
  onExport = null,
  themeUrl = '/themes/standard/Y0_Blank'
}) => {
  const mainContainerRef = useRef(null);
  const settingsContainerRef = useRef(null);
  const widgetsContainerRef = useRef(null);
  const builderRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initializeBuilder = async () => {
      try {
        setIsLoading(true);

        // Wait for containers with retry
        let retries = 0;
        while ((!mainContainerRef.current || !settingsContainerRef.current) && retries < 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (!mainContainerRef.current || !settingsContainerRef.current) {
          toast.error('Failed to initialize: Containers not found');
          setIsLoading(false);
          return;
        }

        // Load BuilderJS files
        await loadBuilderJS();
        if (!mounted) return;

        // Set container IDs
        if (!mainContainerRef.current.id) {
          mainContainerRef.current.id = 'main';
        }
        if (!settingsContainerRef.current.id) {
          settingsContainerRef.current.id = 'idDiv3';
        }
        if (widgetsContainerRef.current && !widgetsContainerRef.current.id) {
          widgetsContainerRef.current.id = 'widgets';
        }

        // Wait for DOM
        await new Promise(resolve => setTimeout(resolve, 200));

        // Initialize Builder (as per documentation)
        const builderConfig = {
          mainContainer: '#main',
          settingContainer: '#idDiv3',
          themeUrl: themeUrl,
        };
        
        // Add widgetsContainer if available
        if (widgetsContainerRef.current) {
          builderConfig.widgetsContainer = '#widgets';
        }
        
        const builder = new window.Builder(builderConfig);

        builderRef.current = builder;
        window.builder = builder;

        // Complete loading handler
        const completeLoading = () => {
          setIsLoading(false);
        };

        // Timeout fallback (5 seconds)
        let loadingTimeout = setTimeout(() => {
          completeLoading();
        }, 5000);

        // Event listener (as per documentation)
        if (builder.addEventListener) {
          builder.addEventListener('loaded', function(event) {
            clearTimeout(loadingTimeout);
            completeLoading();
          });
        }

        // Load data or create new page
        if (initialData) {
          // Load existing data (as per documentation: builder.load(data))
          if (builder.load.length >= 3) {
            builder.load(initialData, themeUrl, () => {
              clearTimeout(loadingTimeout);
              completeLoading();
            });
          } else {
            builder.load(initialData);
            // Fallback: complete after short delay
            setTimeout(() => {
              clearTimeout(loadingTimeout);
              completeLoading();
            }, 1500);
          }
        } else {
          // Create new page (as per documentation: builder.init(theme))
          if (typeof builder.init === 'function') {
            try {
              builder.init(themeUrl);
              // Fallback: complete after short delay
              setTimeout(() => {
                clearTimeout(loadingTimeout);
                completeLoading();
              }, 1500);
            } catch (e) {
              // If init fails, use load fallback - load theme name from index.json
              (async () => {
                try {
                  const themeIndexUrl = `${themeUrl}/index.json`;
                  const response = await fetch(themeIndexUrl);
                  let themeName = 'Blank';
                  
                  if (response.ok) {
                    const themeData = await response.json();
                    themeName = themeData.name || 'Blank';
                  }
                  
                  const emptyData = {
                    theme: themeName,
                    elementLists: [],
                    formats: {}
                  };
                  
                  if (builder.load.length >= 3) {
                    builder.load(emptyData, themeUrl, () => {
                      clearTimeout(loadingTimeout);
                      completeLoading();
                    });
                  } else {
                    builder.load(emptyData, themeUrl);
                    setTimeout(() => {
                      clearTimeout(loadingTimeout);
                      completeLoading();
                    }, 1500);
                  }
                } catch (error) {
                  // Fallback if fetch fails
                  const emptyData = {
                    theme: 'Blank',
                    elementLists: [],
                    formats: {}
                  };
                  if (builder.load.length >= 3) {
                    builder.load(emptyData, themeUrl, () => {
                      clearTimeout(loadingTimeout);
                      completeLoading();
                    });
                  } else {
                    builder.load(emptyData, themeUrl);
                    setTimeout(() => {
                      clearTimeout(loadingTimeout);
                      completeLoading();
                    }, 1500);
                  }
                }
              })();
            }
          } else {
            // Fallback if init() doesn't exist - load theme name from index.json
            (async () => {
              try {
                const themeIndexUrl = `${themeUrl}/index.json`;
                const response = await fetch(themeIndexUrl);
                let themeName = 'Blank';
                
                if (response.ok) {
                  const themeData = await response.json();
                  themeName = themeData.name || 'Blank';
                }
                
                const emptyData = {
                  theme: themeName,
                  elementLists: [],
                  formats: {}
                };
                
                if (builder.load.length >= 3) {
                  builder.load(emptyData, themeUrl, () => {
                    clearTimeout(loadingTimeout);
                    completeLoading();
                  });
                } else {
                  builder.load(emptyData, themeUrl);
                  setTimeout(() => {
                    clearTimeout(loadingTimeout);
                    completeLoading();
                  }, 1500);
                }
              } catch (error) {
                // Fallback if fetch fails
                const emptyData = {
                  theme: 'Blank',
                  elementLists: [],
                  formats: {}
                };
                if (builder.load.length >= 3) {
                  builder.load(emptyData, themeUrl, () => {
                    clearTimeout(loadingTimeout);
                    completeLoading();
                  });
                } else {
                  builder.load(emptyData, themeUrl);
                  setTimeout(() => {
                    clearTimeout(loadingTimeout);
                    completeLoading();
                  }, 1500);
                }
              }
            })();
          }
        }

      } catch (error) {
        toast.error('Failed to initialize BuilderJS');
        setIsLoading(false);
      }
    };

    initializeBuilder();

    return () => {
      mounted = false;
      if (window.builder) {
        delete window.builder;
      }
    };
  }, [initialData, themeUrl]);

  // Save function (as per documentation: builder.getJson())
  const handleSave = () => {
    if (!builderRef.current) {
      toast.error('Builder not initialized');
      return;
    }

    try {
      const data = builderRef.current.getJson();
      if (onSave) {
        onSave(data);
      } else {
        toast.success('Page saved successfully');
      }
    } catch (error) {
      toast.error('Failed to save page');
    }
  };

  // Export function (as per documentation: builder.getHtml())
  const handleExport = () => {
    if (!builderRef.current) {
      toast.error('Builder not initialized');
      return;
    }

    try {
      const html = builderRef.current.getHtml();
      if (onExport) {
        onExport(html);
      } else {
        // Download HTML
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'exported-page.html';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Page exported successfully');
      }
    } catch (error) {
      toast.error('Failed to export page');
    }
  };

  if (isLoading) {
    return (
      <div className="builderjs6-loading">
        <div className="builderjs6-loading-spinner"></div>
        <p>Loading BuilderJS 6.0...</p>
      </div>
    );
  }

  return (
    <div className="builderjs6-wrapper">
      {/* Header */}
      <div className="builderjs6-header">
        <div className="builderjs6-header-left">
          <button className="builderjs6-layout-btn">
            <span className="material-icons">edit</span>
            1 Column Layout
          </button>
          <button className="builderjs6-theme-btn">
            <span className="material-icons">refresh</span>
            Change Theme
          </button>
        </div>

        <div className="builderjs6-header-center">
          <div className="builderjs6-device-preview">
            <button className="builderjs6-device-btn active" title="Desktop">
              <span className="material-icons">computer</span>
            </button>
            <button className="builderjs6-device-btn" title="Tablet">
              <span className="material-icons">tablet</span>
            </button>
            <button className="builderjs6-device-btn" title="Mobile">
              <span className="material-icons">phone_android</span>
            </button>
          </div>
        </div>

        <div className="builderjs6-header-right">
          <button className="builderjs6-action-btn clear">Clear</button>
          <button className="builderjs6-action-btn reset">
            <span className="material-icons">refresh</span>
            Reset
          </button>
          <button className="builderjs6-action-btn save" onClick={handleSave}>
            <span className="material-icons">save</span>
            Save
          </button>
          <button className="builderjs6-action-btn download">
            <span className="material-icons">download</span>
            Download bundle
          </button>
          <button className="builderjs6-action-btn export" onClick={handleExport}>
            <span className="material-icons">file_download</span>
            Export
            <span className="material-icons">arrow_drop_down</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="builderjs6-content">
        {/* Canvas */}
        <div className="builderjs6-canvas-wrapper">
          <div 
            ref={mainContainerRef}
            id="main"
            className="builderjs6-main-container"
          ></div>
        </div>

        {/* Widgets Sidebar */}
        <div className="builderjs6-sidebar">
          <div className="builderjs6-widgets-header">
            <div className="builderjs6-widgets-tabs">
              <button className="builderjs6-tab-btn active">
                <span className="material-icons">grid_view</span>
              </button>
              <button className="builderjs6-tab-btn">
                <span className="material-icons">settings</span>
              </button>
              <button className="builderjs6-tab-btn">
                <span className="material-icons">palette</span>
              </button>
              <button className="builderjs6-tab-btn">
                <span className="material-icons">menu</span>
              </button>
            </div>
          </div>
          <div className="builderjs6-widgets-content">
            <div className="builderjs6-widgets-hint">
              <span className="material-icons">auto_awesome</span>
              Just drag & drop the widget into the page content to insert!
            </div>
            <div 
              ref={widgetsContainerRef}
              id="widgets"
              className="builderjs6-widgets-container"
            ></div>
          </div>
        </div>

        {/* Settings Panel */}
        <div className="builderjs6-settings-panel">
          <div 
            ref={settingsContainerRef}
            id="idDiv3"
            className="builderjs6-settings-container"
          ></div>
        </div>
      </div>
    </div>
  );
};

export default BuilderJS6Simple;

